import type { PropsWithChildren } from 'react'
import { useState, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useId } from '../hooks/useId'

type PortalProps =
  | {
      isMenu?: never
      open: boolean
      parent?: never
    }
  | {
      isMenu?: true
      open: boolean
      parent?: Node | null
    }

function usePortalRoot() {
  const id = `coconut-portal-root-${useId()}`

  return useState(() => {
    if (typeof window === 'undefined') return null
    const existingRoot = document.getElementById(id)
    if (existingRoot) return existingRoot

    const root = document.createElement('div')
    root.setAttribute('id', id)

    return document.body.appendChild(root)
  })[0]
}

function Portal({
  children,
  isMenu,
  open,
  parent,
}: PropsWithChildren<PortalProps>): JSX.Element | null {
  const target = usePortalRoot()
  const [element] = useState<HTMLDivElement | null>(() =>
    typeof window === 'undefined' ? null : document.createElement('div'),
  )

  useLayoutEffect(() => {
    if (!target || !element || (isMenu && !parent)) return

    if (isMenu) {
      parent?.appendChild(element)
    } else {
      target.appendChild(element)
    }

    return () => {
      if (!target || !element) return

      if (isMenu) {
        parent?.removeChild(element)
      } else {
        target.removeChild(element)

        if (target.childNodes.length <= 0) {
          target.parentElement?.removeChild(target)
        }
      }
    }
  }, [target, element, isMenu, parent])

  useLayoutEffect(() => {
    if (element) {
      element.style.display = open ? 'block' : 'none'
    }
  }, [element, open])

  return !target || !element || (isMenu && !parent)
    ? null
    : createPortal(children, element)
}

export default Portal
